<script setup lang="ts">
import { ref } from 'vue';
import Datepicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';
import {
  startOfDay,
  endOfDay,
  subDays,
  subWeeks,
  startOfWeek,
  endOfWeek,
  subMonths,
  startOfMonth,
  endOfMonth,
  subYears,
  startOfYear,
  endOfYear,
} from 'date-fns';
import gettext from '@/i18n/gettext';

const { $gettext } = gettext;

// Each preset's value is generated lazily so that ranges ending "now"
// (e.g. Today, This Week) always reflect the current client time at click
// time instead of the time the component was first mounted.
const presetConfigs: { label: string; getValue: () => [Date, Date] }[] = [
  {
    label: $gettext('Today'),
    getValue: () => [startOfDay(new Date()), new Date()],
  },
  {
    label: $gettext('Yesterday'),
    getValue: () => [
      startOfDay(subDays(new Date(), 1)),
      endOfDay(subDays(new Date(), 1)),
    ],
  },
  {
    label: $gettext('This Week'),
    getValue: () => [startOfWeek(new Date(), { weekStartsOn: 1 }), new Date()],
  },
  {
    label: $gettext('Last Week'),
    getValue: () => [
      startOfWeek(subWeeks(new Date(), 1), { weekStartsOn: 1 }),
      endOfWeek(subWeeks(new Date(), 1), { weekStartsOn: 1 }),
    ],
  },
  {
    label: $gettext('Last Month'),
    getValue: () => [
      startOfMonth(subMonths(new Date(), 1)),
      endOfMonth(subMonths(new Date(), 1)),
    ],
  },
  {
    label: $gettext('Last Year'),
    getValue: () => [
      startOfYear(subYears(new Date(), 1)),
      endOfYear(subYears(new Date(), 1)),
    ],
  },
];

const presetDates = ref(
  presetConfigs.map((p) => ({
    label: p.label,
    value: p.getValue(),
    slot: 'preset-date-range-button',
  })),
);

// Returns a freshly computed range for the clicked preset so time-based
// ranges (Today, This Week) are recalculated on every click.
function getPresetValue(label: string, fallback: [Date, Date]): [Date, Date] {
  const config = presetConfigs.find((p) => p.label === label);
  return config ? config.getValue() : fallback;
}

defineProps<{
  dateRange: [Date, Date] | null;
}>();

const emit = defineEmits<{
  (e: 'update:dateRange', value: [Date, Date] | null): void;
}>();

// Tracks the picker's in-progress (not yet applied) selection so we can
// detect an incomplete range when the menu closes.
const internalDateRange = ref<Date[] | null>(null);

// Upper bound for selectable dates. Refreshed whenever the picker opens so it
// never goes stale (combined with `ignore-time-validation` it only constrains
// the day, allowing "now" to always be selected).
const maxDate = ref(new Date());

function onOpen() {
  maxDate.value = new Date();
}

function onDateChange(value: [Date, Date] | null) {
  emit('update:dateRange', value);
}

function onInternalModelChange(value: Date[] | null) {
  internalDateRange.value = value;
}

// If the user picks only a start date and clicks outside the dialog without
// choosing an end date, complete the range using the current client time so
// the selection is always valid.
function onClosed() {
  const { value } = internalDateRange;
  if (Array.isArray(value) && value[0] && !value[1]) {
    onDateChange([value[0], new Date()]);
  }
  internalDateRange.value = null;
}
</script>

<template>
  <Datepicker
    :model-value="dateRange"
    range
    :format="'yyyy-MM-dd HH:mm:ss'"
    :clearable="true"
    :max-date="maxDate"
    :ignore-time-validation="true"
    :preset-dates="presetDates"
    @update:modelValue="onDateChange"
    @internal-model-change="onInternalModelChange"
    @open="onOpen"
    @closed="onClosed"
  >
    <template #preset-date-range-button="{ label, value, presetDate }">
      <span
        class="dp__btn dp--preset-range"
        role="button"
        :tabindex="0"
        @click="presetDate(getPresetValue(label, value))"
        @keyup.enter.prevent="presetDate(getPresetValue(label, value))"
        @keyup.space.prevent="presetDate(getPresetValue(label, value))"
      >
        {{ label }}
      </span>
    </template>
  </Datepicker>
</template>

<style scoped lang="scss">
:deep(.dp__input) {
  height: 40px;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  padding: 0 12px;
  font-size: 14px;
  background-color: white;
  display: flex;
  align-items: center;
  min-width: 300px;

  &:hover {
    border-color: rgba(0, 0, 0, 0.24);
  }

  &:focus {
    border-color: var(--v-primary-base);
  }

  .dp__input_wrap {
    flex: 1;
  }
}

:deep(.dp__input_wrap input) {
  text-align: center;
}

:deep(.dp__theme_light) {
  --dp-background-color: white;
  --dp-text-color: rgba(0, 0, 0, 0.87);
  --dp-hover-color: rgba(0, 0, 0, 0.04);
  --dp-hover-text-color: rgba(0, 0, 0, 0.87);
  --dp-hover-icon-color: rgba(0, 0, 0, 0.87);
  --dp-primary-color: rgb(var(--v-theme-primary));
  --dp-primary-text-color: white;
  --dp-secondary-color: rgba(0, 0, 0, 0.12);
  --dp-border-color: rgba(0, 0, 0, 0.12);
  --dp-menu-border-color: rgba(0, 0, 0, 0.12);
  --dp-border-color-hover: rgba(0, 0, 0, 0.24);
  --dp-disabled-color: rgba(0, 0, 0, 0.38);
  --dp-scroll-bar-background: #f3f3f3;
  --dp-scroll-bar-color: #959595;
  --dp-success-color: #4caf50;
  --dp-success-color-disabled: #81c784;
  --dp-icon-color: rgba(0, 0, 0, 0.54);
  --dp-danger-color: #ff5252;
  --dp-highlight-color: rgba(0, 0, 0, 0.04);
}

:deep(.dp__menu) {
  border-radius: 12px;
}
:deep(.dp__preset_ranges) {
  border-right: 1px solid var(--dp-border-color);
  padding: 8px;

  .dp__preset_range {
    padding: 8px 12px;
    border-radius: 4px;
    cursor: pointer;
    transition: background-color 0.2s;

    &:hover {
      background-color: var(--dp-hover-color);
    }
  }
}

:deep(.dp__calendar_header) {
  padding: 8px;
}

:deep(.dp__calendar_header_separator) {
  margin: 0 8px;
}

:deep(.dp__calendar_header_item) {
  font-weight: 500;
}

:deep(.dp__cell_inner) {
  border-radius: 4px;

  &:hover {
    background-color: var(--dp-hover-color);
  }
}

:deep(.dp__today) {
  border: 1px solid var(--dp-primary-color);
}

:deep(.dp__active_date) {
  background-color: var(--dp-primary-color) !important;
  color: var(--dp-primary-text-color) !important;
  font-weight: 500;
}

:deep(.dp__arrow_btn) {
  &:hover {
    background-color: var(--dp-hover-color);
  }
}

:deep(.dp__action_buttons) {
  padding: 8px;
  border-top: 1px solid var(--dp-border-color);
  display: flex;
  flex-direction: column;
  gap: 8px;

  .dp__selection_preview {
    padding: 8px;
    text-align: center;
    color: var(--dp-text-color);
    font-size: 14px;
    white-space: normal;
    word-break: break-word;
    line-height: 1.4;
  }

  .dp__action_buttons_wrapper {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
  }

  .dp__action_button {
    padding: 8px 16px;
    border-radius: 4px;
    font-weight: 500;
    transition: background-color 0.2s;

    &:hover {
      background-color: var(--dp-hover-color);
    }

    &.dp__action_select {
      background-color: var(--dp-primary-color);
      color: var(--dp-primary-text-color);

      &:hover {
        background-color: var(--dp-primary-color);
        opacity: 0.9;
      }
    }
  }
}
</style>
